import type { VoiceTranscriptionError, VoiceTranscriptionHandlers, VoiceTranscriptionProvider, VoiceTranscriptionSession } from "../application/voice-transcription.js";
import { extractVoiceFinishCommand } from "../application/voice-commands.js";

interface SpeechAlternativeLike { transcript: string }
interface SpeechResultLike { readonly isFinal: boolean; readonly length: number; readonly [index: number]: SpeechAlternativeLike }
interface SpeechResultListLike { readonly length: number; readonly [index: number]: SpeechResultLike }
interface SpeechResultEventLike extends Event { resultIndex: number; results: SpeechResultListLike }
interface SpeechErrorEventLike extends Event { error: string }

interface BrowserSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
  onaudioend: (() => void) | null;
  onspeechstart: (() => void) | null;
  onspeechend: (() => void) | null;
  onsoundstart: (() => void) | null;
  onsoundend: (() => void) | null;
  onresult: ((event: SpeechResultEventLike) => void) | null;
  onerror: ((event: SpeechErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;
const SILENCE_TIMEOUT_MS = 4_500;
const START_TIMEOUT_MS = 5_000;
const RESULT_TIMEOUT_MS = 15_000;
type SpeechWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

function recognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  const speechWindow = window as SpeechWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

function mapError(error: string): VoiceTranscriptionError {
  if (error === "not-allowed" || error === "service-not-allowed" || error === "audio-capture") return "permission";
  if (error === "no-speech") return "no-speech";
  if (error === "network") return "network";
  if (error === "aborted") return "aborted";
  return "unknown";
}

export class WebSpeechTranscriptionProvider implements VoiceTranscriptionProvider {
  isSupported(): boolean {
    return recognitionConstructor() !== null;
  }

  start(language: string, handlers: VoiceTranscriptionHandlers): VoiceTranscriptionSession {
    const Recognition = recognitionConstructor();
    if (!Recognition) {
      queueMicrotask(() => handlers.onError("unsupported"));
      return { stop() {}, abort() {} };
    }

    const recognition = new Recognition();
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    let listening = true;
    let acceptingResults = true;
    let audioReady = false;
    let soundActive = false;
    let notifyEnd = true;
    let endNotified = false;
    let receivedText = false;
    let pendingInterim = "";
    let silenceTimer: number | null = null;
    let resultTimer: number | null = null;
    let endFallbackTimer: number | null = null;
    let startTimer: number | null = null;

    const clearSilenceTimer = () => {
      if (silenceTimer !== null) window.clearTimeout(silenceTimer);
      silenceTimer = null;
    };
    const clearResultTimer = () => {
      if (resultTimer !== null) window.clearTimeout(resultTimer);
      resultTimer = null;
    };
    const clearEndFallback = () => {
      if (endFallbackTimer !== null) window.clearTimeout(endFallbackTimer);
      endFallbackTimer = null;
    };
    const clearStartTimer = () => {
      if (startTimer !== null) window.clearTimeout(startTimer);
      startTimer = null;
    };
    const clearTimers = () => {
      clearSilenceTimer(); clearResultTimer(); clearEndFallback(); clearStartTimer();
    };
    const fail = (error: VoiceTranscriptionError) => {
      if (!notifyEnd || endNotified) return;
      listening = false;
      acceptingResults = false;
      notifyEnd = false;
      clearTimers();
      try { recognition.abort(); } catch { /* Already disconnected. */ }
      handlers.onError(error);
    };
    const finishSession = () => {
      if (endNotified || !notifyEnd) return;
      endNotified = true;
      listening = false;
      acceptingResults = false;
      clearTimers();
      if (pendingInterim) handlers.onFinal(pendingInterim);
      pendingInterim = "";
      handlers.onEnd();
    };
    const stopRecognition = () => {
      if (!listening) return;
      listening = false;
      acceptingResults = false;
      clearTimers();
      handlers.onCaptureEnd?.();
      if (pendingInterim) {
        handlers.onFinal(pendingInterim);
        pendingInterim = "";
      }
      if (!notifyEnd || endNotified) return;
      endFallbackTimer = window.setTimeout(finishSession, 1_200);
      try { recognition.stop(); } catch { finishSession(); }
    };
    const stopAfterSilence = () => {
      clearSilenceTimer();
      silenceTimer = window.setTimeout(() => {
        if (!listening) return;
        if (!receivedText) fail("no-speech");
        else stopRecognition();
      }, SILENCE_TIMEOUT_MS);
    };
    const waitForResult = () => {
      clearResultTimer();
      resultTimer = window.setTimeout(() => {
        if (listening) fail("no-speech");
      }, RESULT_TIMEOUT_MS);
    };
    startTimer = window.setTimeout(() => fail("unknown"), START_TIMEOUT_MS);
    // Service startup alone does not confirm that the microphone is capturing.
    recognition.onstart = () => {};
    recognition.onaudiostart = () => {
      if (!listening || !notifyEnd || audioReady) return;
      audioReady = true;
      clearStartTimer();
      handlers.onListening();
      if (listening) stopAfterSilence();
    };
    const soundStarted = () => {
      if (!listening || !audioReady) return;
      soundActive = true;
      clearSilenceTimer();
      // Bound a live audio stream whose recognition service produces no text.
      if (resultTimer === null) waitForResult();
    };
    const soundEnded = () => {
      if (!listening || !audioReady) return;
      soundActive = false;
      stopAfterSilence();
    };
    recognition.onspeechstart = soundStarted;
    recognition.onsoundstart = soundStarted;
    recognition.onspeechend = soundEnded;
    recognition.onsoundend = soundEnded;
    recognition.onaudioend = () => {
      if (!listening || !notifyEnd) return;
      if (!audioReady) { fail("unknown"); return; }
      listening = false;
      clearTimers();
      handlers.onCaptureEnd?.();
      // Natural audio termination can precede the final recognition result.
      endFallbackTimer = window.setTimeout(() => {
        if (receivedText) finishSession();
        else fail("no-speech");
      }, 1_200);
    };

    recognition.onresult = event => {
      if (!acceptingResults || !notifyEnd || endNotified) return;
      let interim = "";
      let final = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript ?? "";
        if (result?.isFinal) final += transcript;
        else interim += transcript;
      }
      const interimText = interim.trim();
      const finalText = final.trim();
      if (interimText || finalText) receivedText = true;
      let finishRequested = false;
      if (finalText) {
        pendingInterim = "";
        const finish = extractVoiceFinishCommand(finalText);
        finishRequested ||= finish.finished;
        if (finish.text) handlers.onFinal(finish.text);
      }
      if (interimText) {
        const finish = extractVoiceFinishCommand(interimText);
        finishRequested ||= finish.finished;
        pendingInterim = finish.text;
        if (finish.text) handlers.onInterim(finish.text);
      }
      if (finishRequested) stopRecognition();
      else if (listening && (interimText || finalText)) {
        if (!soundActive) stopAfterSilence();
        waitForResult();
      }
    };
    recognition.onerror = event => {
      if (!notifyEnd || endNotified) return;
      if (event.error === "no-speech" && receivedText) {
        handlers.onCaptureEnd?.();
        finishSession();
        return;
      }
      fail(mapError(event.error));
    };
    recognition.onend = () => {
      if (!notifyEnd || endNotified) return;
      handlers.onCaptureEnd?.();
      // Never restart invisibly: speech during the restart gap would be lost.
      if (listening && !receivedText) { fail(audioReady ? "no-speech" : "unknown"); return; }
      if (acceptingResults && !receivedText) { fail("no-speech"); return; }
      finishSession();
    };

    try {
      recognition.start();
    } catch {
      queueMicrotask(() => fail("unknown"));
    }

    return {
      stop: stopRecognition,
      abort: () => { clearTimers(); listening = false; acceptingResults = false; notifyEnd = false; try { recognition.abort(); } catch { /* Already disconnected. */ } },
    };
  }
}

export const webSpeechTranscription = new WebSpeechTranscriptionProvider();
